import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, finalize, map, switchMap } from 'rxjs';
import { PlanoResumo, ProdutoPlano } from '../../../core/models/parametros/plano.model';
import { PlanoInstitutosService } from '../../../core/services/parametros/plano-institutos.service';
import { CodigoPlanoEmUsoError, PlanosService, SUFIXO_COPIA } from '../../../core/services/parametros/planos.service';
import { ToastService } from '../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import {
  ColunaOrdenacaoPlanos,
  FILTRO_PLANOS_VAZIO,
  FiltroPlanos,
  janelaPaginas,
  opcaoProduto,
  produtoDaOpcao,
  produtosLivres,
  rotuloProduto,
} from '../../../core/utils/parametros/planos.util';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { TrilhaNavegacaoComponent } from '../../../shared/components/trilha-navegacao/trilha-navegacao.component';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestListaVisivelDirective } from '../../../shared/directives/select-vivest/vivest-lista-visivel.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';

interface ColunaTabela {
  readonly chave: ColunaOrdenacaoPlanos;
  readonly rotulo: string;
}

/** Opção do menu de ações (⋮) de uma linha. */
export interface OpcaoMenuPlano {
  readonly id: 'editar' | 'duplicar' | 'status';
  readonly rotulo: string;
  readonly icone: string;
  /** Mudar o status fica separado das demais por uma linha, por alterar o plano de imediato. */
  readonly separada?: boolean;
}

/**
 * Posição do menu na tela. Ele é `position: fixed` para não ser cortado pela rolagem horizontal
 * da tabela; abre para cima quando não cabe abaixo do botão.
 */
interface PosicaoMenu {
  readonly top: number | null;
  readonly bottom: number | null;
  readonly right: number;
}

/** Altura aproximada do menu (3 opções + separador), usada para decidir se abre para cima. */
const ALTURA_MENU = 150;
const DISTANCIA_MENU = 4;

/** Elementos do design system lidos pelos filtros (valor atual do campo). */
type CampoComValor = HTMLElement & { value: string };

/** Opções do "Mostrar 10 | 50 | 100 Itens por página" do rodapé. */
export const TAMANHOS_PAGINA = [10, 50, 100] as const;

/**
 * Tela "Parâmetros de Plano": lista os planos cadastrados com filtro por código, nome e status e
 * paginação, ambos aplicados pela API; as ações do menu ⋮ editam, duplicam ou ativam/inativam o plano.
 */
@Component({
  selector: 'app-planos',
  standalone: true,
  imports: [TrilhaNavegacaoComponent, VivestListaVisivelDirective, VivestModalAreaVisivelDirective, EmptyStateComponent],
  // Web components do design system Vivest (input, select, button, icon-button, icon, tag).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './planos.component.html',
  styleUrl: './planos.component.scss',
  host: {
    '(document:click)': 'fecharMenuAoClicarFora($event)',
    '(window:resize)': 'fecharMenu()',
    '(window:scroll)': 'fecharMenu()',
  },
})
export class PlanosComponent implements OnInit {
  private readonly planosService = inject(PlanosService);
  private readonly institutosService = inject(PlanoInstitutosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Botão ⋮ que abriu o menu, para devolver o foco a ele. */
  private gatilhoMenu: HTMLElement | null = null;

  /** Trilha da listagem: "Planos › Parâmetros de Plano" (o último passo é a página atual). */
  readonly trilha = ['Planos', 'Parâmetros de Plano'];
  readonly colunas: readonly ColunaTabela[] = [
    { chave: 'id', rotulo: 'ID Parametrização' },
    { chave: 'codigo', rotulo: 'Código do Plano' },
    { chave: 'nome', rotulo: 'Nome do Plano' },
    { chave: 'status', rotulo: 'Status' },
    { chave: 'ultimaAtualizacao', rotulo: 'Última Atualização' },
  ];
  readonly statusOpcoes = [
    { value: 'todos', label: 'Todos' },
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Inativo', label: 'Inativo' },
  ];
  readonly tamanhosPagina = TAMANHOS_PAGINA;

  readonly planos = signal<PlanoResumo[]>([]);
  readonly carregando = signal(true);

  /** Valores digitados nos campos; só filtram a lista ao clicar em "Pesquisar". */
  readonly rascunho = signal<FiltroPlanos>({ ...FILTRO_PLANOS_VAZIO });
  readonly filtroAplicado = signal<FiltroPlanos>({ ...FILTRO_PLANOS_VAZIO });
  readonly pagina = signal(1);
  readonly tamanhoPagina = signal<number>(TAMANHOS_PAGINA[0]);

  /** Id do plano com o menu de ações aberto (no máximo um por vez). */
  readonly menuAberto = signal<number | null>(null);
  /** Ação do menu aguardando confirmação no modal ("Duplicar" ou "Inativar"/"Ativar"). */
  readonly confirmacao = signal<{ acao: 'duplicar' | 'status'; plano: PlanoResumo } | null>(null);
  /**
   * Plano que recebe a cópia no modal de "Duplicar": uma parametrização por produto, então só entram os
   * produtos ainda sem parametrização; o código da cópia é o do produto no Amadeus.
   */
  readonly produtosDestino = signal<ProdutoPlano[]>([]);
  readonly destinoCopia = signal<ProdutoPlano | null>(null);
  readonly erroDestinoCopia = signal('');
  readonly opcoesDestino = computed(() =>
    produtosLivres(this.produtosDestino()).map(opcaoProduto),
  );
  readonly menuPosicao = signal<PosicaoMenu>({ top: null, bottom: null, right: 0 });

  readonly totalPaginas = signal(1);
  /** Até 5 números de página, com a atual no meio; « e » levam às pontas. */
  readonly paginas = computed(() => janelaPaginas(this.pagina(), this.totalPaginas()));

  readonly mensagemListaVazia = computed(() =>
    this.carregando() ? 'Carregando planos…' : 'Nenhum plano encontrado com esses filtros.',
  );

  ngOnInit(): void {
    this.carregarPlanos();
  }

  /** Planos vigentes (/v1/planos) para o destino do "Duplicar"; recarregados a cada abertura do modal. */
  private carregarProdutosDestino(): void {
    this.planosService
      .listarProdutos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (produtos) => this.produtosDestino.set(produtos),
        error: () => this.erroDestinoCopia.set('Não foi possível carregar a lista de planos.'),
      });
  }

  /** Busca na API a página atual com os filtros aplicados. */
  private carregarPlanos(): void {
    this.carregando.set(true);
    this.loaderService.showLoader();
    this.planosService
      .listarPlanos(this.filtroAplicado(), this.pagina(), this.tamanhoPagina())
      .pipe(
        finalize(() => this.carregando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ itens, totalPaginas }) => {
          this.planos.set(itens);
          this.totalPaginas.set(Math.max(1, totalPaginas));
          this.esconderLoaderAposLista();
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar os planos.', 'error');
        },
      });
  }

  /**
   * O loader só some com a lista inteira na tela: espera o Angular renderizar as linhas e cada
   * web component do design system nelas (tags de status, botões ⋮, ícones da paginação)
   * concluir a própria renderização.
   */
  private esconderLoaderAposLista(): void {
    afterNextRender(
      () => {
        void this.aguardarComponentesDaLista().finally(() => this.loaderService.hideLoader());
      },
      { injector: this.injector },
    );
  }

  private async aguardarComponentesDaLista(): Promise<void> {
    const componentes = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement & { updateComplete?: Promise<unknown> }>(
        '[data-testid="planos-painel-lista"] :is(vivest-tag, vivest-icon-button, vivest-icon)',
      ),
    );
    // Sem o design system registrado, `updateComplete` não existe e não há o que esperar.
    await Promise.all(componentes.map((componente) => componente.updateComplete));
  }

  /** "Novo Plano" abre o cadastro na aba "Dados do Plano". */
  novoPlano(): void {
    void this.router.navigate(['/planos/novo']);
  }

  // ── filtros ─────────────────────────────────────────────────────────────────

  atualizarRascunho(campo: keyof FiltroPlanos, event: Event): void {
    const valor = (event.target as CampoComValor).value ?? '';
    this.rascunho.update((atual) => ({ ...atual, [campo]: valor }));
  }

  pesquisar(): void {
    this.filtroAplicado.set({ ...this.rascunho() });
    this.pagina.set(1);
    this.carregarPlanos();
  }

  limpar(): void {
    this.rascunho.set({ ...FILTRO_PLANOS_VAZIO });
    this.filtroAplicado.set({ ...FILTRO_PLANOS_VAZIO });
    this.pagina.set(1);
    this.carregarPlanos();
  }

  // ── tabela ──────────────────────────────────────────────────────────────────

  /** "PL001 - Plano CD II". */
  identificacao(plano: PlanoResumo): string {
    return `${plano.codigo} - ${plano.nome}`;
  }

  varianteStatus(plano: PlanoResumo): 'success' | 'warning' {
    return plano.status === 'Ativo' ? 'success' : 'warning';
  }

  data(plano: PlanoResumo): string {
    return formatDateIsoToBr(plano.ultimaAtualizacao);
  }

  // ── menu de ações ───────────────────────────────────────────────────────────

  opcoesDe(plano: PlanoResumo): readonly OpcaoMenuPlano[] {
    const ativo = plano.status === 'Ativo';
    return [
      { id: 'editar', rotulo: 'Editar', icone: 'edit-2' },
      { id: 'duplicar', rotulo: 'Duplicar', icone: 'copy' },
      { id: 'status', rotulo: ativo ? 'Inativar' : 'Ativar', icone: ativo ? 'slash' : 'check-circle', separada: true },
    ];
  }

  /** Abre o menu da linha (fechando o de outra, se houver) ou fecha se já estiver aberto. */
  alternarMenu(plano: PlanoResumo, event: Event): void {
    if (this.menuAberto() === plano.id) {
      this.fecharMenu(true);
      return;
    }

    const gatilho = event.currentTarget as HTMLElement;
    const area = gatilho.getBoundingClientRect();
    const cabeAbaixo = area.bottom + DISTANCIA_MENU + ALTURA_MENU <= window.innerHeight;

    this.gatilhoMenu = gatilho;
    this.menuPosicao.set({
      top: cabeAbaixo ? area.bottom + DISTANCIA_MENU : null,
      bottom: cabeAbaixo ? null : window.innerHeight - area.top + DISTANCIA_MENU,
      right: window.innerWidth - area.right,
    });
    this.menuAberto.set(plano.id);
    afterNextRender(() => this.itensMenu()[0]?.focus(), { injector: this.injector });
  }

  /**
   * "Editar" abre a parametrização do plano; "Duplicar" e "Inativar"/"Ativar" pedem confirmação
   * no modal antes de seguir com a ação.
   */
  escolherOpcao(plano: PlanoResumo, opcao: OpcaoMenuPlano): void {
    if (opcao.id === 'editar') {
      this.fecharMenu();
      void this.router.navigate(['/planos', plano.id, 'editar']);
      return;
    }
    this.fecharMenu(true);
    this.destinoCopia.set(null);
    this.erroDestinoCopia.set('');
    if (opcao.id === 'duplicar') {
      this.carregarProdutosDestino();
    }
    this.confirmacao.set({ acao: opcao.id, plano });
  }

  // ── confirmação de duplicar / ativar / inativar ─────────────────────────────

  /** Status para o qual o plano em confirmação vai mudar ("Inativar"/"Ativar"). */
  private readonly novoStatus = computed<PlanoResumo['status']>(() =>
    this.confirmacao()?.plano.status === 'Ativo' ? 'Inativo' : 'Ativo',
  );

  /** Verbo da ação em confirmação: rótulo do botão e base do título. */
  readonly acaoConfirmacao = computed(() => {
    if (this.confirmacao()?.acao === 'duplicar') {
      return 'Duplicar';
    }
    return this.novoStatus() === 'Inativo' ? 'Inativar' : 'Ativar';
  });
  readonly tituloConfirmacao = computed(() => `${this.acaoConfirmacao()} plano`);
  readonly descricaoConfirmacao = computed(() => {
    const confirmacao = this.confirmacao();
    if (!confirmacao) {
      return '';
    }
    const { plano, acao } = confirmacao;
    if (acao === 'duplicar') {
      return (
        `A cópia "${plano.nome}${SUFIXO_COPIA}" leva as configurações e os institutos do plano ${this.identificacao(plano)} ` +
        'e é criada inativa. Escolha o plano que vai recebê-la (só aparecem os ainda sem parametrização).'
      );
    }
    return `Deseja ${this.acaoConfirmacao().toLowerCase()} o plano ${this.identificacao(plano)}?`;
  });

  cancelarConfirmacao(): void {
    this.confirmacao.set(null);
  }

  /** Plano de destino escolhido na lista do modal de "Duplicar". */
  selecionarDestino(event: Event): void {
    const opcao = (event as CustomEvent<{ value: string }>).detail;
    this.destinoCopia.set(produtoDaOpcao(this.produtosDestino(), opcao?.value));
    this.erroDestinoCopia.set('');
  }

  /** Digitar no campo desfaz a escolha anterior até um plano da lista ser escolhido de novo. */
  digitarDestino(event: Event): void {
    const destino = this.destinoCopia();
    if (!destino || (event.target as CampoComValor).value !== rotuloProduto(destino)) {
      this.destinoCopia.set(null);
    }
  }

  /** "Duplicar" fica com o modal aberto até a cópia ser criada, para trocar o destino se preciso. */
  confirmarAcao(): void {
    const confirmacao = this.confirmacao();
    if (!confirmacao) {
      return;
    }
    if (confirmacao.acao === 'duplicar') {
      this.duplicar(confirmacao.plano);
      return;
    }
    const status = this.novoStatus();
    this.confirmacao.set(null);
    this.alterarStatus(confirmacao.plano, status);
  }

  private alterarStatus(plano: PlanoResumo, status: PlanoResumo['status']): void {
    const verbo = status === 'Ativo' ? 'ativar' : 'inativar';
    this.executar(this.planosService.alterarStatus(plano.id, status), (atualizado) => {
      this.planos.update((planos) =>
        planos.map((item) => (item.id === atualizado.id ? atualizado : item)),
      );
      this.toastService.show(`Plano ${this.identificacao(atualizado)} ${status === 'Ativo' ? 'ativado' : 'inativado'}.`, 'success');
    }, `Não foi possível ${verbo} o plano.`);
  }

  /** Duplica o plano para o destino escolhido e leva junto toda a configuração de institutos dele. */
  private duplicar(plano: PlanoResumo): void {
    const destino = this.destinoCopia();
    if (!destino) {
      this.erroDestinoCopia.set('Selecione na lista o plano que vai receber a cópia.');
      return;
    }
    const duplicacao = this.planosService
      .duplicarPlano(plano, destino)
      .pipe(switchMap((copia) => this.institutosService.copiarInstitutos(plano.id, copia.id).pipe(map(() => copia))));
    this.executar(duplicacao, (copia) => {
      this.confirmacao.set(null);
      this.toastService.show(`Plano ${this.identificacao(copia)} criado.`, 'success');
      this.carregarPlanos();
    }, (erro) => {
      if (erro instanceof CodigoPlanoEmUsoError) {
        this.erroDestinoCopia.set('Este plano já está parametrizado.');
        return;
      }
      this.confirmacao.set(null);
      this.toastService.show('Não foi possível duplicar o plano.', 'error');
    });
  }

  /** Roda a ação no service com o loader; a falha vira toast ou o tratamento informado. */
  private executar(
    acao: Observable<PlanoResumo>,
    sucesso: (plano: PlanoResumo) => void,
    falha: string | ((erro: unknown) => void),
  ): void {
    this.loaderService.showLoader();
    acao
      .pipe(
        finalize(() => this.loaderService.hideLoader()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: sucesso,
        error: (erro: unknown) => (typeof falha === 'string' ? this.toastService.show(falha, 'error') : falha(erro)),
      });
  }


  /** Fecha o menu; `devolverFoco` leva o foco de volta ao botão ⋮ (teclado e escolha de opção). */
  fecharMenu(devolverFoco = false): void {
    if (this.menuAberto() === null) {
      return;
    }
    this.menuAberto.set(null);
    if (devolverFoco) {
      this.gatilhoMenu?.focus();
    }
    this.gatilhoMenu = null;
  }

  /** Setas, Home e End percorrem as opções; Esc fecha; Tab fecha e segue o fluxo normal. */
  navegarMenu(event: KeyboardEvent): void {
    const itens = this.itensMenu();
    const atual = itens.indexOf(document.activeElement as HTMLElement);
    const destinos: Record<string, number> = {
      ArrowDown: (atual + 1) % itens.length,
      ArrowUp: (atual - 1 + itens.length) % itens.length,
      Home: 0,
      End: itens.length - 1,
    };

    if (event.key in destinos) {
      event.preventDefault();
      itens[destinos[event.key]]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.fecharMenu(true);
    } else if (event.key === 'Tab') {
      this.fecharMenu();
    }
  }

  /** Clique fora do menu e do botão que o abriu fecha o menu. */
  fecharMenuAoClicarFora(event: Event): void {
    const caminho = event.composedPath();
    const dentro = caminho.some(
      (alvo) => alvo === this.gatilhoMenu || (alvo instanceof HTMLElement && alvo.classList.contains('planos__menu')),
    );
    if (!dentro) {
      this.fecharMenu();
    }
  }

  private itensMenu(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.planos__menu [role="menuitem"]'));
  }

  // ── paginação ───────────────────────────────────────────────────────────────

  irParaPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas() && pagina !== this.pagina()) {
      this.pagina.set(pagina);
      this.carregarPlanos();
    }
  }

  /** Trocar a quantidade por página volta para a primeira página. */
  alterarTamanhoPagina(tamanho: number): void {
    if (tamanho !== this.tamanhoPagina()) {
      this.tamanhoPagina.set(tamanho);
      this.pagina.set(1);
      this.carregarPlanos();
    }
  }
}
